import { v4 as uuidv4 } from 'uuid';

import { EventEmitter } from '@angular/core';
import { FormControl, FormRecord, Validators } from '@angular/forms';

import { Subscription, debounceTime, filter } from 'rxjs';

import { QuestionStateService } from '@core/services';

import { exportFraction, fillForm, generateActivations, generateControl } from '@/tax-detail/utils';

import { FileUtil } from './file-util.type';
import { QuestionField } from './question-field.model';
import { QuestionType } from './question-type.enum';
import { NestedQuestion } from './question.model';
import { TaxResponseDocument } from './tax-response-document.model';
import { TaxResponseGroup } from './tax-response-group.model';
import { TaxResponseRecord } from './tax-response-record.model';

function getDebounce(fieldType: QuestionType): number {
    const DEBOUNCE_TIME = 200;
    const SHORT_DEBOUNCE_TIME = 25;
    if ([QuestionType.Check, QuestionType.Radio, QuestionType.Select, QuestionType.File, QuestionType.SingleFile].includes(fieldType)) {
        return SHORT_DEBOUNCE_TIME;
    }
    return DEBOUNCE_TIME;
}

export class FormUtil {
    id: string;
    index?: number;
    bufferedFiles = [] as FileUtil[];
    uuidMap = {} as Record<string, string>;
    originalData?: TaxResponseGroup;
    defaultData?: TaxResponseGroup;
    defaultDataMap?: Record<string, any>;
    question: NestedQuestion;
    form?: FormRecord;
    fieldActivated = {} as Record<number, boolean>;
    fieldActivatedChanged = new EventEmitter<void>();
    nestedUtils?: Record<number, FormUtil[]>;
    editable = true;
    protected stateService: QuestionStateService;
    protected untrackedChanges = [] as string[];
    protected activationChanges = [] as Subscription[];

    constructor(
        id: string,
        question: NestedQuestion,
        state: QuestionStateService,
        canEdit?: boolean,
        group?: TaxResponseGroup,
        defaultGroup?: TaxResponseGroup,
        ...otherGroups: (TaxResponseGroup | undefined)[]
    ) {
        this.id = id;
        this.stateService = state;
        this.index = group?.index;
        this.originalData = group;
        this.question = question;
        this.editable = canEdit ?? true;
        this.defaultData = defaultGroup;
        //Signature with otherGroups indicates accountant, so setup should not be used
        //simply overriding setup is not enough. If called from here, the result will be degraded to a FormUtil instead of FormUtilAccountant
        if (otherGroups.length === 0) {
            this.setup(...otherGroups);
            this.setupDefaultValues();
        }
    }

    protected setup(...groups: (TaxResponseGroup | undefined)[]) {
        this.clearGroupFromState();
        this.form = this.generateForm(this.originalData);
        this.resetUuidMap();
        let nestedUtils = undefined as Record<number, FormUtil[]> | undefined;
        this.question.inputFields
            ?.filter((field) => field.type == QuestionType.NestedQuestion)
            .forEach((field) => {
                nestedUtils = nestedUtils ?? {};
                nestedUtils[field.id] = [];
            });
        if (this.originalData !== undefined) {
            Object.keys(nestedUtils ?? {}).forEach((key) => {
                const nestedQuestion = this.question.inputFields!.find((field) => field.id.toString() == key)!.nestedQuestion!;
                const rec = this.originalData!.records.find((rec) => rec.inputField.toString() == key);
                const defaultRec = this.defaultData?.records.find((rec) => rec.inputField.toString() === key);
                rec?.nestedGroups?.forEach((nestedGroup) => {
                    // Match based on group index - will this always work?
                    const defaultGroup = nestedQuestion.multi
                        ? defaultRec?.nestedGroups?.find((defGr) => defGr.index === nestedGroup.index)
                        : defaultRec?.nestedGroups?.[0];
                    nestedUtils![Number(key)].push(
                        new FormUtil(uuidv4(), nestedQuestion, this.stateService, this.editable, nestedGroup, defaultGroup),
                    );
                });
                // Not present in current answer, but should exist!
                if (rec === undefined && !nestedQuestion.multi) {
                    nestedUtils![Number(key)].push(new FormUtil(uuidv4(), nestedQuestion, this.stateService, this.editable));
                }
            });
        } else {
            Object.keys(nestedUtils ?? {}).forEach((key) => {
                const nestedQuestion = this.question.inputFields!.find((field) => field.id.toString() == key)!.nestedQuestion!;
                if (!nestedQuestion.multi) {
                    nestedUtils![Number(key)].push(new FormUtil(uuidv4(), nestedQuestion, this.stateService, this.editable));
                }
            });
        }
        this.nestedUtils = nestedUtils;
        this.fieldActivated = generateActivations(this.question.inputFields ?? [], [this.form], false, this.stateService);
        this.fieldActivatedChanged.emit();
        this.setActivationSubscriptions();
        this.markAsPristine();
    }

    protected setupDefaultValues() {
        if (this.defaultData === undefined) {
            return;
        }
        this.defaultDataMap = {} as Record<string, any>;
        this.defaultData.records
            ?.filter((rec) => rec.nestedGroups === undefined)
            .forEach((rec) => {
                const field = this.question.inputFields?.find((field) => field.id == rec.inputField);
                if (field === undefined) {
                    return;
                }
                if ([QuestionType.File, QuestionType.SingleFile].includes(field.type)) {
                    this.defaultDataMap![field.tag] = (rec.documents ?? []).map((doc) => doc.uuid);
                } else if (field.type === QuestionType.Percentage) {
                    this.defaultDataMap![field.tag] = rec.data != undefined ? Number(rec.data) * 100 : null;
                } else {
                    this.defaultDataMap![field.tag] = rec.data;
                }
            });
    }

    protected setActivationSubscriptions(): void {
        this.cleanUpSelfActivations();

        if (this.form === undefined) {
            return;
        }

        const knownFieldIds = this.question.inputFields?.map((field) => field.id) ?? [];
        let externalDependencies = new Set<number>();
        this.question.inputFields?.forEach((field) => {
            field.conditions
                ?.filter((cond) => !knownFieldIds.includes(cond.dependsOn))
                .forEach((condition) => externalDependencies.add(condition.dependsOn));
            this.activationChanges.push(
                this.form!.controls[field.tag]?.valueChanges.pipe(debounceTime(getDebounce(field.type))).subscribe((value) => {
                    this.stateService.signalFieldUpdate(this.question.id, this.id, field.id, value, this.originalData?.id);
                    this.fieldActivated = this.checkFieldActivations(this.form!, field.id, this.fieldActivated);
                }),
            );
        });
        //Set up listeners for external state and current value
        externalDependencies.forEach((externalFieldId) => {
            this.activationChanges.push(
                this.stateService.stateObservable.pipe(filter((event) => event.fieldId === externalFieldId)).subscribe((newState) => {
                    this.fieldActivated = this.checkFieldActivations(this.form!, newState.fieldId, this.fieldActivated);
                }),
            );
        });

        let idsToCheck = this.question.inputFields?.map((field) => field.id) ?? [];
        idsToCheck.push(...externalDependencies);
        //Setup activations initially
        idsToCheck.forEach((idToCheck) => {
            this.fieldActivated = this.checkFieldActivations(this.form!, idToCheck, this.fieldActivated);
        });
    }

    protected checkFieldActivations(form: FormRecord, fieldId: number, currentActivations: Record<number, boolean>) {
        const allQuestionFields = this.question.inputFields;
        allQuestionFields
            ?.filter((qField) => (qField.conditions?.map((cond) => cond.dependsOn) ?? []).includes(fieldId))
            .forEach((qField) => {
                let allowed = qField.isActivatedBy(form, allQuestionFields, false, this.stateService);
                if (qField.type === QuestionType.HiddenField) {
                    this.form?.controls[qField.tag].setValue(allowed);
                } else if (!allowed && currentActivations[qField.id]) {
                    this.resetField(qField);
                    this.form?.controls[qField.tag]?.disable();
                } else if (qField.readOnly) {
                    this.form?.controls[qField.tag]?.disable();
                } else if (allowed) {
                    if (this.editable) {
                        this.form?.controls[qField.tag]?.enable();
                    }
                    if (!currentActivations[qField.id] && this.originalData !== undefined) {
                        const defaultVal = [QuestionType.Check, QuestionType.Radio].includes(qField.type) ? false : null;
                        const rec = this.originalData.records.find((rec) => rec.inputField == qField.id);
                        this.form?.controls[qField.tag]?.patchValue(rec?.data ?? defaultVal);
                    }
                    if (qField.required) {
                        this.form?.get(qField.tag)?.addValidators(Validators.required);
                        this.form?.get(qField.tag)?.updateValueAndValidity();
                    }
                }
                currentActivations[qField.id] = allowed;
            });
        this.fieldActivatedChanged.emit();
        return currentActivations;
    }

    resetFieldToDefault(field: QuestionField) {
        if (this.form === undefined) {
            console.debug('cannot set default data for non-existing form');
            return;
        }

        if (!Object.keys(this.defaultDataMap ?? {}).includes(field.tag)) {
            console.debug('no default data found for field', field.tag);
            return;
        }

        if (![QuestionType.File, QuestionType.SingleFile].includes(field.type)) {
            this.form!.controls[field.tag].patchValue(this.defaultDataMap![field.tag]);
            this.form!.controls[field.tag].markAsDirty();
            return;
        }

        this.bufferedFiles = this.bufferedFiles.filter(
            (fUtil) => fUtil.fieldId !== field.id || this.defaultDataMap![field.tag].includes(fUtil.uuid),
        );

        const newUuids = this.defaultDataMap![field.tag].filter(
            (uuid: string) => !(this.form!.controls[field.tag]?.value ?? []).includes(uuid),
        );
        const defaultDocuments = this.defaultData!.records.find((rec) => rec.inputField === field.id)?.documents ?? [];
        for (const newUuid of newUuids) {
            const doc = defaultDocuments.find((doc) => doc.uuid === newUuid);
            if (doc !== undefined) {
                this.copyDocumentToBufferedFiles(doc, field);
            }
        }
        this.form!.controls[field.tag].patchValue([...this.defaultDataMap![field.tag]]);
        this.resetUuidMap(this.getFileUuids());
        return;
    }

    protected copyDocumentToBufferedFiles(document: TaxResponseDocument, field: QuestionField): void {
        //Temporary fill with empty file, waiting for fetch, but info can already be used in other code
        this.bufferedFiles.push({ uuid: document.uuid, file: new File([], document.filename), fieldId: field.id });
        fetch(document.url).then((result) => {
            result.blob().then((blob) => {
                this.bufferedFiles.find((bfile) => bfile.uuid === document.uuid)!.file = new File([blob], document.filename);
            });
        });
    }

    protected resetUuidMap(uuids?: string[]) {
        this.uuidMap = {};
        (this.originalData?.getGroupDocuments() ?? [])
            .filter((doc) => uuids === undefined || uuids.includes(doc.uuid))
            .forEach((doc) => {
                this.uuidMap[doc.uuid] = doc.filename;
            });
        this.bufferedFiles
            .filter((fUtil) => uuids === undefined || uuids.includes(fUtil.uuid))
            .forEach((fUtil) => {
                this.uuidMap[fUtil.uuid] = fUtil.file.name;
            });
    }

    protected generateForm(data?: TaxResponseGroup, external = false) {
        let form = new FormRecord({});
        this.question.inputFields?.forEach((field) => {
            if (field.type !== QuestionType.NestedQuestion) {
                const control = generateControl(field);
                form.addControl(field.tag, control);
            }
        });
        if (data !== undefined) {
            fillForm(this.question, form, data, external, this.stateService);
            if (!external) {
                this.question.inputFields?.forEach((field) => {
                    //Insert state after filling the form
                    if (field.type !== QuestionType.NestedQuestion) {
                        this.stateService.signalFieldUpdate(this.question.id, this.id, field.id, form.controls[field.tag]?.value, data.id);
                    }
                });
            }
        }
        return form;
    }

    gatherAllBufferedFiles(): FileUtil[] {
        let res = [] as FileUtil[];
        this.bufferedFiles.forEach((fUtil) => {
            fUtil.location = [{ groupId: this.index ?? -1 }];
            res.push(fUtil);
        });
        if (this.nestedUtils !== undefined) {
            Object.keys(this.nestedUtils).forEach((key) => {
                this.nestedUtils![Number(key)].forEach((util) => {
                    res.push(
                        ...util.gatherAllBufferedFiles().map((fUtil) => {
                            fUtil.location = [{ groupId: this.index ?? -1, nestedField: Number(key) }, ...(fUtil.location ?? [])];
                            return fUtil;
                        }),
                    );
                });
            });
        }
        return res;
    }

    addNestedUtil(idx: number) {
        const field = this.question.inputFields?.find((field) => field.id == idx);
        if (field === undefined) {
            return;
        }
        const uid = uuidv4();
        this.nestedUtils?.[idx].push(new FormUtil(uid, field.nestedQuestion!, this.stateService, this.editable));
        if (this.nestedUtils?.[idx]) {
            this.nestedUtils[idx] = [...this.nestedUtils[idx]];
        }
        this.untrackedChanges.push(uid);
    }

    removeNestedUtil(idx: number, utilId: string) {
        const field = this.question.inputFields?.find((field) => field.id == idx);
        if (field === undefined || this.nestedUtils === undefined) {
            return;
        }
        if (this.untrackedChanges.includes(utilId)) {
            this.untrackedChanges = this.untrackedChanges.filter((val) => val !== utilId);
        } else {
            this.untrackedChanges.push(utilId);
        }
        this.nestedUtils![idx].find((util) => util.id === utilId)?.cleanUpActivations();
        this.nestedUtils![idx].find((util) => util.id === utilId)?.clearGroupFromState();
        this.nestedUtils![idx] = this.nestedUtils[idx].filter((util) => util.id !== utilId);
    }

    resetField(field: QuestionField) {
        if (this.form === undefined) {
            return;
        }
        this.form.get(field.tag)?.patchValue([QuestionType.Check, QuestionType.Radio].includes(field.type) ? false : null);
        this.form.get(field.tag)?.removeValidators(Validators.required);
        this.form.get(field.tag)?.updateValueAndValidity();
        // Clear nested field when resetting
        if (field.type == QuestionType.NestedQuestion && Object.keys(this.nestedUtils ?? {}).some((key) => key === field.id.toString())) {
            this.nestedUtils![field.id] = [new FormUtil(uuidv4(), field.nestedQuestion!, this.stateService, this.editable)];
        }
    }

    protected getFileUuids(): string[] {
        if (this.form === undefined) {
            return [];
        }
        let res = [] as string[];
        this.question.inputFields?.forEach((field) => {
            if ([QuestionType.File, QuestionType.SingleFile].includes(field.type)) {
                const control = this.form!.controls[field.tag];
                res.push(...(control?.value ?? []));
            }
        });
        return res;
    }

    gatherAllFileUuids(): string[] {
        if (this.form === undefined) {
            return [];
        }
        let res = this.getFileUuids();
        Object.keys(this.nestedUtils ?? {}).forEach((key) => {
            this.nestedUtils![Number(key)].forEach((util) => res.push(...util.gatherAllFileUuids()));
        });

        return res;
    }

    generateGroup(index: number): TaxResponseGroup | undefined {
        // Convert the full content of this util (including nested forms etc) to a TaxResponseGroup
        if (this.form === undefined) {
            return undefined;
        }

        this.index = index;
        let group = new TaxResponseGroup().deserialize({ id: this.originalData?.id, index: this.index });
        Object.keys(this.form?.controls ?? []).forEach((key) => {
            let control = this.form!.get(key);
            const field = this.question.inputFields?.find((field) => field.tag == key);
            let data = ![QuestionType.File, QuestionType.SingleFile, QuestionType.SelectDependent].includes(
                field?.type ?? QuestionType.Text,
            )
                ? control?.value ?? undefined
                : null;
            let dependsOn = field?.type == QuestionType.SelectDependent ? control?.value : undefined;
            if (field?.type === QuestionType.Percentage && typeof data === 'number') {
                data /= 100;
            }
            if (field?.type === QuestionType.Fraction && data) {
                data = exportFraction(data);
            }
            if (control instanceof FormControl && field !== undefined && data !== undefined && (this.fieldActivated[field.id] ?? true)) {
                group.records.push(
                    new TaxResponseRecord().deserialize({ 'input_field': field.id, data: data, 'depends_on_group': dependsOn }),
                );
            }
        });
        if (this.nestedUtils === undefined) {
            return group;
        }

        Object.keys(this.nestedUtils)
            .filter((key) => this.fieldActivated[Number(key)] ?? true)
            .forEach((key) => {
                const utils = this.nestedUtils![Number(key)];
                let maxIdx = Math.max(...utils.map((util) => util.index ?? 0)) + 1;
                utils.forEach((util) => {
                    util.index = util.index ?? maxIdx++;
                });
                group.records.push(
                    new TaxResponseRecord().deserialize({
                        'input_field': Number(key),
                        'nested_groups': utils
                            .map((util) => util.generateGroup(util.index!)?.serialize())
                            .filter((obj) => obj !== undefined),
                    }),
                );
            });

        return group;
    }

    protected getNestedUtils<T extends FormUtil>(): T[] {
        let result = [] as T[];
        if (this.nestedUtils !== undefined) {
            Object.keys(this.nestedUtils).forEach((key) => result.push(...(this.nestedUtils![Number(key)] as T[])));
        }
        return result;
    }

    //The functions below generalize form calls to also affect nested utils recursively
    isValid(): boolean {
        return (this.form === undefined || !this.form.invalid) && this.getNestedUtils().every((util) => util.isValid());
    }

    isDirty(): boolean {
        return this.untrackedChanges.length > 0 || (this.form?.dirty ?? false) || this.getNestedUtils().some((util) => util.isDirty());
    }

    markAsPristine(): void {
        this.form?.markAsPristine();
        this.untrackedChanges = [];
        this.getNestedUtils().forEach((util) => util.markAsPristine());
    }

    markAsUntouched(): void {
        this.form?.markAsUntouched();
        this.getNestedUtils().forEach((util) => util.markAsUntouched());
    }

    markAllAsTouched(): void {
        this.form?.markAllAsTouched();
        this.getNestedUtils().forEach((util) => util.markAllAsTouched());
    }

    enable(): void {
        this.form?.enable();
        this.getNestedUtils().forEach((util) => util.enable());
    }

    disable(): void {
        this.form?.disable();
        this.getNestedUtils().forEach((util) => util.disable());
    }

    cleanUpSelfActivations(): void {
        this.activationChanges.forEach((sub) => sub?.unsubscribe());
        this.activationChanges = [];
    }

    cleanUpActivations(): void {
        this.cleanUpSelfActivations();
        this.getNestedUtils().forEach((util) => util.cleanUpActivations());
    }

    clearGroupFromState() {
        this.stateService.clearQuestionGroupState(this.question.id, this.id);
        this.getNestedUtils().forEach((util) => util.clearGroupFromState());
    }
}
